/**
 * Portal Upload - External Vendor File Upload Interface
 *
 * Allows vendors to upload data files for assessment.
 * Token-based authentication - no user session required.
 *
 * Features:
 * - Drag & drop file upload
 * - Multiple file support
 * - Crosswalk file upload
 * - Upload progress tracking
 * - File validation
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-532
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BRANDING } from '../../../config/branding';
import '../../../styles/vendor-portal.css';
import { FileRoleBadge, FileRoleSelector, type FileRole, type RoleAction, type AlternativeRole } from '../../../components/file-roles';

// ============================================================================
// TYPES
// ============================================================================

interface PortalSession {
  assessment_id: string;
  vendor_name: string;
  product_name: string;
  status: string;
  deadline?: string;
  files_uploaded: number;
}

interface UploadedFile {
  id: string;
  filename: string;
  size: number;
  type: string;
  uploaded_at: string;
  status: 'uploading' | 'complete' | 'error';
  progress?: number;
  error?: string;
  // Role detection fields (DC-573)
  role?: FileRole;
  role_confidence?: number;
  role_action?: RoleAction;
  alternative_roles?: AlternativeRole[];
}

interface CrosswalkFile {
  filename: string;
  mappings: number;
  coverage: number;
  validated: boolean;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const FileUploadZone: React.FC<{
  onUpload: (files: FileList) => void;
  uploading: boolean;
  disabled: boolean;
}> = ({ onUpload, uploading, disabled }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };

  return (
    <div
      className={`portal-upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''} ${disabled ? 'disabled' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id="portal-file-upload"
        multiple
        accept=".csv,.tsv,.xlsx,.json,.parquet"
        onChange={handleChange}
        disabled={disabled || uploading}
      />
      <label htmlFor="portal-file-upload" className="upload-label">
        {uploading ? (
          <>
            <div className="spinner" />
            <span className="upload-text">Uploading...</span>
          </>
        ) : (
          <>
            <span className="upload-icon">📁</span>
            <span className="upload-text">
              Drag & drop your data files here
            </span>
            <span className="upload-subtext">
              or <strong>click to browse</strong>
            </span>
            <span className="upload-formats">
              CSV, TSV, XLSX, JSON, Parquet (max 100MB per file)
            </span>
          </>
        )}
      </label>
    </div>
  );
};

const FileList: React.FC<{
  files: UploadedFile[];
  onRemove: (fileId: string) => void;
  onRoleChange?: (fileId: string, newRole: FileRole) => void;
}> = ({ files, onRemove, onRoleChange }) => {
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="portal-file-list">
      <h3>Uploaded Files ({files.length})</h3>
      <ul>
        {files.map((file) => (
          <li key={file.id} className={`file-row ${file.status}`}>
            <span className="file-icon">
              {file.status === 'uploading' ? '⏳' : file.status === 'error' ? '❌' : '✅'}
            </span>
            <div className="file-details">
              <span className="file-name">{file.filename}</span>
              <span className="file-meta">
                {formatSize(file.size)}
                {file.status === 'complete' && ` • Uploaded ${formatDate(file.uploaded_at)}`}
                {file.status === 'error' && ` • ${file.error}`}
              </span>
              {file.status === 'uploading' && file.progress !== undefined && (
                <div className="upload-progress">
                  <div className="progress-bar" style={{ width: `${file.progress}%` }} />
                </div>
              )}
            </div>
            {/* File Role Badge/Selector (DC-573) */}
            {file.status === 'complete' && file.role && (
              <div className="file-role">
                {onRoleChange && file.role_action && (file.role_action === 'suggest' || file.role_action === 'require_user') ? (
                  <FileRoleSelector
                    currentRole={file.role}
                    confidence={file.role_confidence || 0}
                    action={file.role_action}
                    alternativeRoles={file.alternative_roles}
                    onRoleChange={(newRole) => onRoleChange(file.id, newRole)}
                    filename={file.filename}
                  />
                ) : (
                  <FileRoleBadge
                    role={file.role}
                    confidence={file.role_confidence}
                    action={file.role_action}
                    size="sm"
                  />
                )}
              </div>
            )}
            {file.status === 'complete' && (
              <button
                className="btn btn-ghost btn-sm remove-btn"
                onClick={() => onRemove(file.id)}
                title="Remove file"
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const CrosswalkUpload: React.FC<{
  crosswalk: CrosswalkFile | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  uploading: boolean;
}> = ({ crosswalk, onUpload, onRemove, uploading }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div className="crosswalk-section">
      <h3>
        ID Crosswalk File
        <span className="optional-badge">Optional</span>
      </h3>
      <p className="section-description">
        If your data uses proprietary identifiers, upload a CSV mapping them to public ontologies.
        This helps us assess interoperability with standard vocabularies.
      </p>

      {crosswalk ? (
        <div className="crosswalk-info">
          <div className="crosswalk-details">
            <span className="crosswalk-icon">🔗</span>
            <div>
              <span className="crosswalk-filename">{crosswalk.filename}</span>
              <span className="crosswalk-stats">
                {crosswalk.mappings} mappings • {crosswalk.coverage}% coverage
                {crosswalk.validated && ' • Validated ✓'}
              </span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onRemove}>
            Replace
          </button>
        </div>
      ) : (
        <div className="crosswalk-upload">
          <input
            type="file"
            id="crosswalk-upload"
            accept=".csv,.tsv"
            onChange={handleChange}
            disabled={uploading}
          />
          <label htmlFor="crosswalk-upload" className="crosswalk-label">
            {uploading ? (
              <span>Uploading...</span>
            ) : (
              <>
                <span className="crosswalk-icon">📎</span>
                <span>Upload crosswalk file (CSV)</span>
              </>
            )}
          </label>
        </div>
      )}

      <div className="crosswalk-example">
        <h4>Expected Format</h4>
        <pre>
{`proprietary_id,public_ontology,public_id
GENE001,HGNC,HGNC:1234
PROT002,UniProt,P12345
CHEM003,ChEBI,CHEBI:12345`}
        </pre>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const PortalUpload: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<PortalSession | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [crosswalk, setCrosswalk] = useState<CrosswalkFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [crosswalkUploading, setCrosswalkUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validate token and load session
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        navigate('/portal/invalid');
        return;
      }

      try {
        const response = await fetch(`/api/portal/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          navigate(`/portal/${token}`);
          return;
        }

        const data = await response.json();
        setSession(data);

        // Load existing files
        const filesResponse = await fetch(`/api/portal/${token}/files`);
        if (filesResponse.ok) {
          const filesData = await filesResponse.json();
          setFiles(filesData.files || []);
          setCrosswalk(filesData.crosswalk || null);
        }
      } catch {
        setError('Unable to connect to the server.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleFileUpload = useCallback(async (fileList: FileList) => {
    if (!token) return;

    setUploading(true);
    const newFiles: UploadedFile[] = [];

    for (const file of Array.from(fileList)) {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const tempFile: UploadedFile = {
        id: tempId,
        filename: file.name,
        size: file.size,
        type: file.type,
        uploaded_at: new Date().toISOString(),
        status: 'uploading',
        progress: 0,
      };

      setFiles((prev) => [...prev, tempFile]);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`/api/portal/${token}/files`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const result = await response.json();
        setFiles((prev) =>
          prev.map((f) =>
            f.id === tempId
              ? { ...result.file, status: 'complete' as const }
              : f
          )
        );
        newFiles.push(result.file);
      } catch {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === tempId
              ? { ...f, status: 'error' as const, error: 'Upload failed' }
              : f
          )
        );
      }
    }

    setUploading(false);
  }, [token]);

  const handleFileRemove = useCallback(async (fileId: string) => {
    if (!token) return;

    try {
      await fetch(`/api/portal/${token}/files/${fileId}`, {
        method: 'DELETE',
      });
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch {
      setError('Failed to remove file');
    }
  }, [token]);

  // Handle file role change (DC-573)
  const handleFileRoleChange = useCallback(async (fileId: string, newRole: FileRole) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/portal/${token}/files/${fileId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error('Failed to update role');
      }

      const result = await response.json();
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                role: result.file.role,
                role_confidence: 1.0, // User-set = 100% confidence
                role_action: 'auto_assign' as RoleAction,
              }
            : f
        )
      );
    } catch {
      setError('Failed to update file role');
    }
  }, [token]);

  const handleCrosswalkUpload = useCallback(async (file: File) => {
    if (!token) return;

    setCrosswalkUploading(true);

    try {
      const formData = new FormData();
      formData.append('crosswalk', file);

      const response = await fetch(`/api/portal/${token}/crosswalk`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      setCrosswalk(result.crosswalk);
    } catch {
      setError('Failed to upload crosswalk file');
    } finally {
      setCrosswalkUploading(false);
    }
  }, [token]);

  const handleCrosswalkRemove = useCallback(async () => {
    if (!token) return;

    try {
      await fetch(`/api/portal/${token}/crosswalk`, {
        method: 'DELETE',
      });
      setCrosswalk(null);
    } catch {
      setError('Failed to remove crosswalk file');
    }
  }, [token]);

  const handleSubmit = useCallback(async () => {
    if (!token || files.filter((f) => f.status === 'complete').length === 0) {
      setError('Please upload at least one data file before submitting.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`/api/portal/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      // Navigate to review page to show findings and allow vendor responses
      navigate(`/portal/${token}/review`);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token, files, notes, navigate]);

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-loading">
            <div className="spinner" />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const completedFiles = files.filter((f) => f.status === 'complete');
  const canSubmit = completedFiles.length > 0 && !uploading && !submitting && termsAccepted;

  return (
    <div className="portal-page">
      <div className="portal-container wide">
        {/* Header */}
        <div className="portal-header">
          <div className="portal-logo">
            <span className="logo-icon">🧭</span>
            <span className="logo-text">{BRANDING.productName}</span>
          </div>
          <div className="portal-context">
            <span className="vendor-name">{session.vendor_name}</span>
            <span className="product-name">{session.product_name}</span>
          </div>
        </div>

        {/* Progress Steps (Phase 9.1: 4-step progress bar) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0',
          padding: '24px 0',
          marginBottom: '24px',
        }}>
          {[
            { key: 'upload', label: 'Upload Files', number: 1 },
            { key: 'assess', label: 'Assessment', number: 2 },
            { key: 'review', label: 'Review', number: 3 },
            { key: 'submit', label: 'Submit', number: 4 },
          ].map((step, index) => {
            const currentIndex = 0; // Upload is current step (index 0)
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;

            return (
              <React.Fragment key={step.key}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 600,
                    backgroundColor: isCompleted ? '#16a34a' : isCurrent ? '#2563eb' : '#e5e7eb',
                    color: isCompleted || isCurrent ? '#fff' : '#6b7280',
                    border: isCurrent ? '3px solid #93c5fd' : 'none',
                    transition: 'all 0.2s ease',
                  }}>
                    {isCompleted ? '✓' : step.number}
                  </div>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent ? '#2563eb' : isCompleted ? '#16a34a' : '#6b7280',
                  }}>
                    {step.label}
                  </span>
                </div>
                {index < 3 && (
                  <div style={{
                    width: '80px',
                    height: '3px',
                    backgroundColor: index < currentIndex ? '#16a34a' : '#e5e7eb',
                    margin: '0 8px',
                    marginBottom: '28px',
                    transition: 'background-color 0.2s ease',
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="portal-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Main Content */}
        <div className="portal-content">
          <div className="upload-section">
            <h2>Upload Your Data Files</h2>
            <p>
              Upload sample data files from your product. You can upload multiple files.
            </p>

            <FileUploadZone
              onUpload={handleFileUpload}
              uploading={uploading}
              disabled={submitting}
            />

            <FileList files={files} onRemove={handleFileRemove} onRoleChange={handleFileRoleChange} />
          </div>

          <div className="sidebar-section">
            <CrosswalkUpload
              crosswalk={crosswalk}
              onUpload={handleCrosswalkUpload}
              onRemove={handleCrosswalkRemove}
              uploading={crosswalkUploading}
            />

            <div className="notes-section">
              <h3>Additional Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context about your data submission..."
                rows={4}
                disabled={submitting}
              />
            </div>
          </div>
        </div>

        {/* Terms Acceptance */}
        <div className="terms-acceptance">
          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              disabled={submitting}
            />
            <span className="terms-text">
              I confirm that I am authorized to submit this data for assessment and that the information
              provided is accurate and complete. I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="portal-footer-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/portal/${token}`)}
            disabled={submitting}
          >
            ← Back
          </button>
          <div className="submit-section">
            {completedFiles.length > 0 && (
              <span className="file-count">
                {completedFiles.length} file(s) ready
              </span>
            )}
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSubmit}
              disabled={!canSubmit}
              title={!termsAccepted ? 'Please accept the terms to continue' : ''}
            >
              {submitting ? 'Submitting...' : 'Submit for Assessment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalUpload;
